import { PricingStatus, SetPriceGroupDerivationInput } from "~/gql/graphqlBase.generated";
import {
	findReferencePriceGroupIssue,
	REFERENCE_GROUP_INACTIVE_MESSAGE
} from "~fulfill/sales/pricing/helpers/priceLevelFormValidation";

export const DERIVATION_PERCENT_REQUIRED_MESSAGE =
	"Percent off must be between 0 and 100 when a reference group is selected";
export const DERIVATION_REFERENCE_REQUIRED_MESSAGE =
	"Select a reference price group, or clear the percent to remove the derivation";
export const DERIVATION_SELF_MESSAGE = "A price group cannot derive from itself";
export const DERIVATION_INACTIVE_MESSAGE = REFERENCE_GROUP_INACTIVE_MESSAGE;

export const DERIVATION_PRECEDENCE_HELP_TEXT =
	"This group's own price levels apply first. Only items this group doesn't price itself fall back to the reference group's price, minus the percent below.";

export interface PriceGroupDerivationFormData {
	derivedFromPriceGroupId?: string | null;
	derivedPercentOff?: number | null;
}

export type PriceGroupDerivationErrors = Partial<Record<keyof PriceGroupDerivationFormData, string>>;

export interface DerivationCandidateGroup {
	id: string;
	name: string;
	priceId: string;
	status: PricingStatus;
}

export interface DerivationReferenceOption {
	code: string;
	isInactive: boolean;
	name: string;
}

export const formatPriceGroupDerivationLabel = (referenceGroupName: string, percentOff: number): string =>
	`Derived from ${referenceGroupName} −${percentOff}%`;

export const formatPriceGroupDerivationBanner = (referenceGroupName: string, percentOff: number): string =>
	`This group's own price levels apply first. Items it doesn't price itself derive from ${referenceGroupName} at −${percentOff}%.`;

export const describePriceGroupDerivation = (
	group: PriceGroupDerivationFormData,
	priceGroupNameByPriceId: ReadonlyMap<string, string>,
	format: (referenceGroupName: string, percentOff: number) => string
): string | null => {
	if (!group.derivedFromPriceGroupId || group.derivedPercentOff == null) return null;
	return format(
		priceGroupNameByPriceId.get(group.derivedFromPriceGroupId) ?? group.derivedFromPriceGroupId,
		group.derivedPercentOff
	);
};

export interface PriceGroupDerivationCellContext {
	priceGroupNameByPriceId?: ReadonlyMap<string, string>;
}

const EMPTY_PRICE_GROUP_NAME_MAP: ReadonlyMap<string, string> = new Map();

export const resolvePriceGroupDerivationCellValue = (
	data: PriceGroupDerivationFormData | null | undefined,
	context: PriceGroupDerivationCellContext | null | undefined
): string => {
	if (!data) return "";
	return (
		describePriceGroupDerivation(
			data,
			context?.priceGroupNameByPriceId ?? EMPTY_PRICE_GROUP_NAME_MAP,
			formatPriceGroupDerivationLabel
		) ?? ""
	);
};

export const buildDerivationReferenceOptions = (
	priceGroups: readonly DerivationCandidateGroup[],
	excludeBarePriceGroupId?: string | null,
	retainPriceGroupId?: string | null
): DerivationReferenceOption[] =>
	priceGroups
		.filter(
			(group) =>
				group.priceId !== excludeBarePriceGroupId &&
				(group.status === PricingStatus.Active || group.priceId === retainPriceGroupId)
		)
		.map((group) => {
			const isInactive = group.status !== PricingStatus.Active;
			return { code: group.priceId, isInactive, name: isInactive ? `${group.name} (inactive)` : group.name };
		});

export const hasPriceGroupDerivationChanged = (
	loaded: PriceGroupDerivationFormData,
	current: PriceGroupDerivationFormData
): boolean =>
	(loaded.derivedFromPriceGroupId ?? null) !== (current.derivedFromPriceGroupId ?? null) ||
	(loaded.derivedPercentOff ?? null) !== (current.derivedPercentOff ?? null);

// bareGroupId must be the aggregate id (PriceGroupStateData.id), never the tenant-prefixed PriceGroupDocument.id — ValidIdStrict hard-rejects the composite id
export const buildPriceGroupDerivationInput = (
	bareGroupId: string,
	derivation: PriceGroupDerivationFormData
): SetPriceGroupDerivationInput => ({
	derivedFromPriceGroupId: derivation.derivedFromPriceGroupId ?? null,
	derivedPercentOff: derivation.derivedPercentOff ?? null,
	id: bareGroupId
});

const validatePriceGroupDerivation = (
	derivation: PriceGroupDerivationFormData,
	ownBarePriceGroupId?: string | null,
	activeReferenceGroupIds?: ReadonlySet<string>,
	loadedReferencePriceGroupId?: string | null
): PriceGroupDerivationErrors => {
	const errors: PriceGroupDerivationErrors = {};
	const hasReference = !!derivation.derivedFromPriceGroupId?.trim();
	const hasPercent = derivation.derivedPercentOff != null;

	if (hasReference !== hasPercent) {
		if (!hasReference) errors.derivedFromPriceGroupId = DERIVATION_REFERENCE_REQUIRED_MESSAGE;
		if (!hasPercent) errors.derivedPercentOff = DERIVATION_PERCENT_REQUIRED_MESSAGE;
		return errors;
	}

	if (!hasReference) return errors;

	if (derivation.derivedPercentOff == null || derivation.derivedPercentOff < 0 || derivation.derivedPercentOff > 100) {
		errors.derivedPercentOff = DERIVATION_PERCENT_REQUIRED_MESSAGE;
	}

	const referenceIssue = findReferencePriceGroupIssue(
		derivation.derivedFromPriceGroupId,
		ownBarePriceGroupId,
		activeReferenceGroupIds
	);
	if (referenceIssue === "self") {
		errors.derivedFromPriceGroupId = DERIVATION_SELF_MESSAGE;
	} else if (referenceIssue === "inactive" && derivation.derivedFromPriceGroupId !== loadedReferencePriceGroupId) {
		errors.derivedFromPriceGroupId = DERIVATION_INACTIVE_MESSAGE;
	}

	return errors;
};

export default validatePriceGroupDerivation;
